"use client";

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import type { User, Session } from "@supabase/supabase-js";
import { getSupabase } from "@/lib/supabase";
import {
  signInWithPhone as authSignIn,
  verifyOtp as authVerifyOtp,
  signOut as authSignOut,
} from "@/lib/auth";
import { trackSignUp, identifyUser, resetUser } from "@/lib/analytics-user";
import { reportJsError } from "@/lib/js-error-beacon";

interface VedicUser {
  credits: number;
  moon_rashi: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  vedicUser: VedicUser | null;
  loading: boolean;
  signInWithPhone: (phone: string) => Promise<void>;
  verifyOtp: (phone: string, token: string) => Promise<User | null>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export async function fetchVedicUser(userId: string): Promise<VedicUser | null> {
  const { data, error } = await getSupabase()
    .from("vedic_users")
    .select("credits, moon_rashi")
    .eq("id", userId)
    .single();
  if (error) {
    // PGRST116 = "no rows" — normal for a session whose vedic_users row isn't
    // provisioned yet; stay quiet. ANY other error (a schema/contract drift like
    // the 2026-06-25 dropped-`subscription_tier` 400, an RLS misconfig, a transport
    // failure) is a real defect that must not be silently swallowed — feed the owned
    // js_error beacon (events_raw, free, rule 34 / rule-60 bug-layer) so the failure
    // class is queryable next time instead of only flooding the browser console.
    if (error.code !== "PGRST116") {
      reportJsError({
        error_message: `vedic_users fetch failed: ${error.message}`,
        error_name: `supabase_${error.code || "unknown"}`,
        boundary: "AuthProvider.fetchVedicUser",
      });
    }
    return null;
  }
  if (!data) return null;
  return data as VedicUser;
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      window.setTimeout(() => reject(new Error("Auth request timed out")), timeoutMs);
    }),
  ]);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [vedicUser, setVedicUser] = useState<VedicUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Load vedic user data when auth user changes
  const loadVedicUser = useCallback(async (u: User | null) => {
    if (!u) {
      setVedicUser(null);
      return;
    }
    const vu = await fetchVedicUser(u.id);
    setVedicUser(vu);
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Check existing session on mount — and identify the user to
    // PostHog so cross-surface event streams (web ↔ app) share the
    // same distinct_id. This is the linchpin of Package D1: the app
    // already identifies with Supabase user.id via trackSignUp, so
    // mirroring that here unifies funnels across both surfaces.
    withTimeout(getSupabase().auth.getSession(), 2500)
      .then(({ data: { session: s } }) => {
        if (cancelled) return;
        setSession(s);
        setUser(s?.user ?? null);
        if (s?.user) {
          identifyUser(s.user.id, { phone: s.user.phone });
        }
        setLoading(false);
        void loadVedicUser(s?.user ?? null);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Listen for auth state changes
    const {
      data: { subscription },
    } = getSupabase().auth.onAuthStateChange((event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      loadVedicUser(s?.user ?? null);
      if (event === "SIGNED_IN" && s?.user) {
        identifyUser(s.user.id, { phone: s.user.phone });
        // SIGNED_IN fires on every page load with an existing session, not just on
        // first sign-in. Only fire trackSignUp for genuinely new users — detect via
        // created_at == last_sign_in_at (Supabase sets both on initial signup).
        const created = s.user.created_at;
        const lastSignIn = s.user.last_sign_in_at;
        const isNewUser = created && lastSignIn && created === lastSignIn;
        if (isNewUser) {
          trackSignUp({ method: "phone_otp" });
        }
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [loadVedicUser]);

  const signInWithPhone = useCallback(async (phone: string) => {
    await authSignIn(phone);
  }, []);

  const verifyOtp = useCallback(async (phone: string, token: string) => {
    const data = await authVerifyOtp(phone, token);
    // Session will be picked up by onAuthStateChange
    return data.user;
  }, []);

  const signOut = useCallback(async () => {
    await authSignOut();
    setUser(null);
    setSession(null);
    setVedicUser(null);
    resetUser();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        vedicUser,
        loading,
        signInWithPhone,
        verifyOtp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
